// Presentational building blocks for the Messaging Reports page. They mirror
// the chart cards on the Calls page (RhinoVOIPReports) so both reports read
// the same, but are small reusable pieces instead of inline JSX per chart.
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { downloadCsv, categoryColor, REPORT_DIRECTION_COLORS } from '../helpers/ReportsHelpers';
import { sortRows } from '../helpers/MessagingReportsHelpers';

export const GROUPING_OPTIONS = [
  { id: 'total', label: 'Total' },
  { id: 'day', label: 'By Day' },
  { id: 'week', label: 'By Week' },
  { id: 'month', label: 'By Month' },
];

export const ChartCard = ({ title, half, children, csv, actions }) => (
  <div className={`voip-reports__chart-card${half ? ' voip-reports__chart-card--half' : ''}`}>
    <div className="voip-reports__chart-header">
      <h3 className="voip-reports__chart-title">{title}</h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {actions}
        {csv && (
          <button type="button" className="voip-reports__export-btn" onClick={() => downloadCsv(csv.columns, csv.rows, csv.filename)}>
            Export CSV
          </button>
        )}
      </div>
    </div>
    {children}
  </div>
);

ChartCard.propTypes = {
  title: PropTypes.node.isRequired,
  half: PropTypes.bool,
  children: PropTypes.node,
  csv: PropTypes.shape({ columns: PropTypes.array, rows: PropTypes.array, filename: PropTypes.string }),
  actions: PropTypes.node,
};

export const EmptyChart = ({ height }) => (
  <div
    className="voip-reports__chart-empty"
    style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}
  >
    No data for this selection
  </div>
);

EmptyChart.propTypes = { height: PropTypes.number.isRequired };

export const GroupingSelector = ({ value, onChange }) => (
  <div className="voip-reports__grouping-selector">
    {GROUPING_OPTIONS.map((opt) => (
      <button
        key={opt.id}
        type="button"
        className={`voip-reports__grouping-btn${value === opt.id ? ' voip-reports__grouping-btn--active' : ''}`}
        onClick={() => onChange(opt.id)}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

GroupingSelector.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

// Donut with a click-to-hide legend. `colorFor(name, originalIndex)` keeps a
// slice's color stable when other slices are hidden.
// Charts with many legend entries are unreadable: keep the top `maxSeries` and
// fold the rest into one "Other" entry. Table views keep the full detail.
export const MAX_SERIES = 5;
export const OTHER_LABEL = 'Other';
export const OTHER_COLOR = '#B5B7BD';

export function foldOther(entries, maxSeries = MAX_SERIES) {
  if (entries.length <= maxSeries) return entries;
  const sorted = [...entries].sort((a, b) => b.value - a.value);
  const rest = sorted.slice(maxSeries - 1).reduce((sum, e) => sum + e.value, 0);
  return [...sorted.slice(0, maxSeries - 1), { name: OTHER_LABEL, value: rest }];
}

// Every slice gets a call-out. Labels start level with their slice, then neighbours on the same side
// of the donut are pushed apart vertically so thin adjacent slices never stack their text, and an
// elbowed leader line connects each label back to its slice.
const PIE_OUTER_RADIUS = 100;
const PIE_INNER_RADIUS = 50;
const PIE_LABEL_RADIUS = PIE_OUTER_RADIUS + 18; // where a label would sit with no crowding
const PIE_LABEL_GAP = 16; // minimum vertical distance between labels on one side
const PIE_LABEL_BOUND = PIE_OUTER_RADIUS + 25; // keep labels within this distance above/below centre
const PIE_LABEL_X_OFFSET = PIE_OUTER_RADIUS + 30;
const RADIAN = Math.PI / 180;

/**
 * Vertical offsets (from the donut centre) for each slice's label, by slice index. Mirrors recharts'
 * default angles (start at 3 o'clock, counter-clockwise, no padding) so it can be computed up front.
 */
export function layoutPieLabels(values) {
  const total = values.reduce((sum, v) => sum + v, 0);
  const offsets = new Array(values.length).fill(null);
  if (!total) return offsets;
  const sides = { left: [], right: [] };
  let angle = 0;
  values.forEach((value, index) => {
    const sweep = (value / total) * 360;
    const mid = angle + (sweep / 2);
    angle += sweep;
    if (!value) return;
    const side = Math.cos(mid * RADIAN) >= 0 ? 'right' : 'left';
    sides[side].push({ index, y: -Math.sin(mid * RADIAN) * PIE_LABEL_RADIUS });
  });
  Object.values(sides).forEach((labels) => {
    const sorted = [...labels].sort((a, b) => a.y - b.y);
    const ys = sorted.map((label) => label.y);
    // Push down from the top, then back up from the bottom if that ran past the chart.
    for (let i = 0; i < ys.length; i += 1) {
      ys[i] = Math.max(ys[i], i === 0 ? -PIE_LABEL_BOUND : ys[i - 1] + PIE_LABEL_GAP);
    }
    for (let i = ys.length - 1; i >= 0; i -= 1) {
      ys[i] = Math.min(ys[i], i === ys.length - 1 ? PIE_LABEL_BOUND : ys[i + 1] - PIE_LABEL_GAP);
    }
    sorted.forEach(({ index }, i) => { offsets[index] = ys[i]; });
  });
  return offsets;
}

const formatPiePercent = (percent) => {
  const pct = percent * 100;
  return pct > 0 && pct < 1 ? '<1' : Math.round(pct);
};

const renderPieCallout = (offsets) => ({ cx, cy, midAngle, index, name, percent, fill }) => {
  const dy = offsets[index];
  if (dy === null || dy === undefined) return null;
  const cos = Math.cos(-midAngle * RADIAN);
  const sin = Math.sin(-midAngle * RADIAN);
  const isRight = cos >= 0;
  const start = { x: cx + (PIE_OUTER_RADIUS * cos), y: cy + (PIE_OUTER_RADIUS * sin) };
  const elbow = { x: cx + ((PIE_OUTER_RADIUS + 10) * cos), y: cy + ((PIE_OUTER_RADIUS + 10) * sin) };
  const labelX = cx + (isRight ? PIE_LABEL_X_OFFSET : -PIE_LABEL_X_OFFSET);
  const labelY = cy + dy;
  const lineEndX = labelX + (isRight ? -4 : 4);
  return (
    <g key={`callout-${name}`}>
      <path
        d={`M${start.x},${start.y}L${elbow.x},${elbow.y}L${lineEndX},${labelY}`}
        stroke={fill}
        fill="none"
      />
      <text x={labelX} y={labelY} fill={fill} textAnchor={isRight ? 'start' : 'end'} dominantBaseline="central">
        {`${name} ${formatPiePercent(percent)}%`}
      </text>
    </g>
  );
};

export function percentOfTotal(value, total) {
  if (!total) return 0;
  const pct = (value / total) * 100;
  return pct > 0 && pct < 1 ? '<1' : Math.round(pct);
}

export const DonutChart = ({ data: fullData, colorFor: colorForProp, height, valueLabel }) => {
  const [hidden, setHidden] = useState(() => new Set());
  const data = useMemo(() => foldOther(fullData), [fullData]);
  const colorFor = (name, i) => (name === OTHER_LABEL ? OTHER_COLOR : colorForProp(name, i));
  const visible = useMemo(() => data.filter((d) => !hidden.has(d.name)), [data, hidden]);
  const visibleTotal = useMemo(() => visible.reduce((sum, d) => sum + d.value, 0), [visible]);
  const legendPayload = useMemo(() => data.map((d, i) => ({
    value: hidden.has(d.name) ? d.name : `${d.name} ${percentOfTotal(d.value, visibleTotal)}%`,
    color: colorFor(d.name, i),
    type: 'circle',
    inactive: hidden.has(d.name),
    id: d.name,
  })), [data, hidden, visibleTotal, colorForProp]); // eslint-disable-line react-hooks/exhaustive-deps
  const labelOffsets = useMemo(() => layoutPieLabels(visible.map((d) => d.value)), [visible]);
  const toggle = (entry) => {
    // The legend text carries the percentage, so map it back to the slice name.
    const name = entry?.id ?? legendPayload.find((p) => p.value === entry?.value)?.id ?? entry?.value;
    if (!name) return;
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };
  if (!data.length) return <EmptyChart height={height} />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={visible}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={PIE_OUTER_RADIUS}
          innerRadius={PIE_INNER_RADIUS}
          isAnimationActive={false}
          label={renderPieCallout(labelOffsets)}
          labelLine={false}
        >
          {visible.map((entry) => (
            <Cell key={entry.name} fill={colorFor(entry.name, data.findIndex((d) => d.name === entry.name))} />
          ))}
        </Pie>
        <Tooltip formatter={(value) => [value, valueLabel]} />
        <Legend payload={legendPayload} onClick={toggle} />
      </PieChart>
    </ResponsiveContainer>
  );
};

DonutChart.propTypes = {
  data: PropTypes.array.isRequired,
  colorFor: PropTypes.func.isRequired,
  height: PropTypes.number,
  valueLabel: PropTypes.string,
};

DonutChart.defaultProps = { height: 300, valueLabel: 'Messages' };

// Horizontal bars, one per category row. `bars` = [{ key, name, color }];
// with a single bar and no color each row gets its own category color.
export const HorizontalBars = ({ data, bars, formatter, domain }) => {
  const height = Math.max(300, data.length * 40);
  if (!data.length) return <EmptyChart height={height} />;
  const single = bars.length === 1 && !bars[0].color;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" allowDecimals={false} domain={domain} tickFormatter={formatter} />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={180} />
        <Tooltip formatter={formatter} />
        {bars.length > 1 && <Legend />}
        {bars.map((bar) => (
          <Bar key={bar.key} dataKey={bar.key} name={bar.name} fill={bar.color} radius={[0, 4, 4, 0]}>
            {single && data.map((entry, i) => <Cell key={entry.name} fill={categoryColor(i)} />)}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

HorizontalBars.propTypes = {
  data: PropTypes.array.isRequired,
  bars: PropTypes.array.isRequired,
  formatter: PropTypes.func,
  domain: PropTypes.array,
};

// Vertical stacked bars by a label key (hour, weekday, date).
export const StackedBars = ({ data, xKey, bars, stackId, height, interval, allowDecimals }) => {
  if (!data.some((d) => bars.some((b) => d[b.key] > 0))) return <EmptyChart height={height} />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xKey} tick={{ fontSize: 12 }} interval={interval} />
        <YAxis allowDecimals={allowDecimals} />
        <Tooltip />
        <Legend />
        {bars.map((bar, i) => (
          <Bar
            key={bar.key}
            dataKey={bar.key}
            name={bar.name}
            fill={bar.color}
            stackId={stackId}
            radius={i === bars.length - 1 ? [4, 4, 0, 0] : undefined}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

StackedBars.propTypes = {
  data: PropTypes.array.isRequired,
  xKey: PropTypes.string.isRequired,
  bars: PropTypes.array.isRequired,
  stackId: PropTypes.string.isRequired,
  height: PropTypes.number,
  interval: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  allowDecimals: PropTypes.bool, // hourly averages are often below 1
};

StackedBars.defaultProps = { height: 300, interval: 'preserveEnd', allowDecimals: false };

// One line per series key with a click-to-hide legend.
export const MultiLine = ({ data, series, height, formatter }) => {
  const [hidden, setHidden] = useState(() => new Set());
  const toggle = (entry) => {
    const key = entry?.dataKey;
    if (!key) return;
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  if (!series.length || !data.length) return <EmptyChart height={height} />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
        <YAxis allowDecimals={false} />
        <Tooltip formatter={formatter} />
        <Legend onClick={toggle} />
        {series.map((s, i) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.name}
            stroke={s.color || categoryColor(i)}
            strokeWidth={2}
            dot={false}
            connectNulls
            hide={hidden.has(s.key)}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

MultiLine.propTypes = {
  data: PropTypes.array.isRequired,
  series: PropTypes.array.isRequired,
  height: PropTypes.number,
  formatter: PropTypes.func,
};

MultiLine.defaultProps = { height: 400 };

// Sortable table used by every Table view. `columns` = [{ key, label, format? }].
export const SortableTable = ({ columns, rows, defaultSort, defaultAsc, rowKey }) => {
  const [sortColumn, setSortColumn] = useState(defaultSort);
  const [sortAsc, setSortAsc] = useState(defaultAsc);
  const sorted = useMemo(() => sortRows(rows, sortColumn, sortAsc), [rows, sortColumn, sortAsc]);
  const handleSort = (key) => {
    if (key === sortColumn) setSortAsc((prev) => !prev);
    else { setSortColumn(key); setSortAsc(true); }
  };
  return (
    <div className="voip-reports__table-container">
      <table className="voip-reports__table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className="voip-reports__th--sortable" onClick={() => handleSort(col.key)}>
                {col.label}
                <span className={`voip-reports__sort-arrows${sortColumn === col.key ? ' voip-reports__sort-arrows--active' : ''}`}>
                  <span className={sortColumn === col.key && sortAsc ? 'voip-reports__sort-arrow--current' : ''}>&#9650;</span>
                  <span className={sortColumn === col.key && !sortAsc ? 'voip-reports__sort-arrow--current' : ''}>&#9660;</span>
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr><td colSpan={columns.length} style={{ textAlign: 'center', color: '#888' }}>No data for this selection</td></tr>
          )}
          {sorted.map((row, idx) => (
            <tr key={rowKey ? rowKey(row, idx) : idx}>
              {columns.map((col) => <td key={col.key}>{col.format ? col.format(row) : row[col.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

SortableTable.propTypes = {
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  defaultSort: PropTypes.string.isRequired,
  defaultAsc: PropTypes.bool,
  rowKey: PropTypes.func,
};

SortableTable.defaultProps = { defaultAsc: false };

// CSV column list from table columns (same keys/labels/formatters).
export const csvColumns = (columns) => columns.map((c) => ({ key: c.key, header: c.label, format: c.format }));

// Average staff reply time per hour of day; the bar color fades for hours with
// few replies so a single slow reply at 6 am does not read like a trend.
export const ResponseByHourChart = ({ data, height }) => {
  const maxReplies = Math.max(1, ...data.map((d) => d.replies || 0));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="hour" tick={{ fontSize: 12 }} interval={1} />
        <YAxis allowDecimals={false} unit=" min" />
        <Tooltip formatter={(value, name, item) => [`${value} min (${item?.payload?.replies || 0} replies)`, 'Avg. response']} />
        <Bar dataKey="avgResponseMinutes" name="Avg. Response (min)" fill={REPORT_DIRECTION_COLORS.Inbound} radius={[4, 4, 0, 0]}>
          {data.map((entry) => (
            <Cell key={entry.hour} fill={REPORT_DIRECTION_COLORS.Inbound} fillOpacity={0.35 + 0.65 * Math.min(1, (entry.replies || 0) / maxReplies)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

ResponseByHourChart.propTypes = { data: PropTypes.array.isRequired, height: PropTypes.number };
ResponseByHourChart.defaultProps = { height: 300 };
